import type { ProductApiResponse, ProductItem } from './products';

/**
 * ローカル開発用のデフォルトAPI。
 *
 * - フロントとAPIを同一ドメインで配信する場合は不要（相対パスでOK）
 * - ローカルで API を :3000、フロントを :5173 などで動かす場合に使えます
 */
const DEFAULT_LOCAL_API_BASE_URL = 'http://localhost:3000';

const toFiniteNumberOrZero = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const normalizeProduct = (raw: unknown, fallbackItemCode = ''): ProductItem => {
  const data = (raw ?? {}) as Record<string, unknown>;

  return {
    id: String(data.id ?? data.item_code ?? fallbackItemCode).trim(),
    sku: String(data.sku ?? '').trim(),
    title: String(data.title ?? ''),
    brand: String(data.brand ?? ''),
    price: toFiniteNumberOrZero(data.price),
    image: String(data.image ?? ''),
    url: String(data.url ?? ''),
  };
};

/**
 * APIのベースURLを決定します。
 * - window.__PRODUCT_API_BASE_URL__ があればそれを優先
 * - localhost のときは DEFAULT_LOCAL_API_BASE_URL
 * - それ以外は相対パス（同一オリジン）
 */
export function getApiBaseUrl(): string {
  const w = window as any;
  if (w && typeof w.__PRODUCT_API_BASE_URL__ === 'string' && w.__PRODUCT_API_BASE_URL__.length > 0) {
    return w.__PRODUCT_API_BASE_URL__;
  }

  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  return isLocal ? DEFAULT_LOCAL_API_BASE_URL : '';
}

/**
 * Aurora(MySQL)のデータを返すAPIにアクセスして商品情報を取得します。
 */
export async function fetchProductByItemCode(itemCode: string): Promise<ProductItem> {
  const base = getApiBaseUrl();
  const url = `${base}/api/product?item_code=${encodeURIComponent(itemCode)}`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`Failed to fetch product. status=${res.status} ${res.statusText} body=${body}`);
  }

  const raw = (await res.json()) as ProductApiResponse;
  return normalizeProduct(raw, itemCode);
}

/**
 * 1つのMattertagに複数の商品が紐づくケース向け。
 * - 取得に失敗した商品はスキップします（他が表示できるように）
 * - 全て失敗した場合は空配列
 */
export async function fetchProductsByItemCodes(itemCodes: string[]): Promise<ProductItem[]> {
  const unique = Array.from(new Set(itemCodes.map((s) => String(s ?? '').trim()).filter(Boolean)));
  if (unique.length === 0) return [];

  const results = await Promise.all(
    unique.map(async (code) => {
      try {
        return await fetchProductByItemCode(code);
      } catch (e) {
        console.warn('Failed to fetch product for item_code=', code, e);
        return null;
      }
    })
  );

  return results.filter((v): v is ProductItem => Boolean(v));
}

/**
 * 条件検索（自動）: 指定したカラム=値（AND条件）でヒットした中から
 * updated_at_for_sort_latest が最新の1件を返します。
 *
 * API 仕様（フロントからの呼び出し）:
 *   GET /api/product?latest=1&gender=WOMENS&brand_name_kana=タトラス
 *
 * - 返却に失敗した場合は null
 */
export type FilterValue = string | string[];

/**
 * 条件検索（自動）: ヒットした中から最新N件を返します。
 * API 仕様:
 *   GET /api/product?latest=1&limit=10&gender=WOMENS&category_code=knit-sweater,sweat
 */
export async function fetchLatestProductsByFilters(
  filters: Record<string, FilterValue>,
  limit: number
): Promise<ProductItem[]> {
  const base = getApiBaseUrl();

  const safeLimit = Math.max(1, Math.min(20, Number(limit) || 1));

  const params = new URLSearchParams();
  params.set('latest', '1');
  params.set('limit', String(safeLimit));

  for (const [k, v] of Object.entries(filters)) {
    const key = String(k ?? '').trim();
    if (!key) continue;

    if (Array.isArray(v)) {
      const joined = v.map((s) => String(s ?? '').trim()).filter(Boolean).join(',');
      if (!joined) continue;
      params.set(key, joined);
    } else {
      const value = String(v ?? '').trim();
      if (!value) continue;
      params.set(key, value);
    }
  }

  // latest + limit しか無い = 条件が無い
  if (Array.from(params.keys()).length <= 2) return [];

  const url = `${base}/api/product?${params.toString()}`;
  const res = await fetch(url, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!res.ok) {
    const body = await res.text().catch(() => '');
    console.warn('Failed to fetch latest products by filters.', { status: res.status, body, filters, safeLimit });
    return [];
  }

  const raw = (await res.json()) as ProductApiResponse | ProductApiResponse[];
  const list = Array.isArray(raw) ? raw : [raw];

  return list
    .map((item) => normalizeProduct(item))
    .filter((item) => item.id.length > 0);
}

export async function fetchLatestProductByFilters(filters: Record<string, FilterValue>): Promise<ProductItem | null> {
  const list = await fetchLatestProductsByFilters(filters, 1);
  return list[0] ?? null;
}
